/// <amd-module name="Feed/Legacy/Protocols/NuGet/NuGetPackageProtocol" />
import * as React from "react";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { PackageCommandIds } from "Feed/Common/Constants/Constants";
import { FeedRole, FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { IGeneralDialogProps } from "Feed/Common/Types/IGeneralDialogProps";
import { BowtieIconProps } from "Feed/Common/Utils/Icons";
import { getFullyQualifiedFeedId } from "Feed/Legacy//Helpers/FeedNameResolver";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { isV2Feed } from "Feed/Legacy/Helpers/FeedCapabilityHelper";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { BatchGenerator, IBatchIterator, TransformingBatchGenerator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { DeleteCommandHelper } from "Feed/Legacy/Protocols/Common/DeleteCommandHelper";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol, IPackageProtocolClient, isPackage } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import {
    DownloadPackageCommand,
    PromotePackageCommand,
    PromotePackagesCommand
} from "Feed/Legacy/Protocols/Common/PackageCommands";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { RelistPackageCommand, RelistPackagesCommand } from "Feed/Legacy/Protocols/NuGet/Commands/NuGetRelistCommand";
import { UnlistPackageCommand, UnlistPackagesCommand } from "Feed/Legacy/Protocols/NuGet/Commands/NuGetUnlistCommand";
import { NuGetKey } from "Feed/Legacy/Protocols/NuGet/Constants/NuGetConstants";
import { NuGetClientTool } from "Feed/Legacy/Protocols/NuGet/NuGetClientTool";
import { IFeedNuGetDataService } from "Feed/Legacy/Protocols/NuGet/NuGetDataService";
import { NuGetOverviewAttributesPanel } from "Feed/Legacy/Protocols/NuGet/NuGetOverviewAttributesPanel";
import { NuGetOverviewContentPanel } from "Feed/Legacy/Protocols/NuGet/NuGetOverviewContentPanel";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed, projId } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export class NuGetPackageProtocol implements IPackageProtocol {
    private static readonly _icon = new BowtieIconProps("brand-nuget");

    public readonly name = "NuGet";
    public readonly key = "NuGet";
    get vssIconProps() {
        return NuGetPackageProtocol._icon;
    }
    public supportedCommandsMask: ProtocolCommands =
        // tslint:disable-next-line:no-bitwise
        ProtocolCommands.Promote |
        ProtocolCommands.Delete |
        ProtocolCommands.Download |
        ProtocolCommands.Unlist |
        ProtocolCommands.Relist |
        ProtocolCommands.Restore |
        ProtocolCommands.PermanentDelete;
    public readonly clients: IPackageProtocolClient[] = [];

    private pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;
        this.clients.push(new NuGetClientTool(this.pageContext));
    }

    get NuGetDataService(): IFeedNuGetDataService {
        return this.pageContext.getService<IFeedNuGetDataService>("IFeedNuGetDataService");
    }

    public getPackageMessage(packageSummary: Package, packageVersion: PackageVersion): string {
        if (packageVersion && !packageVersion.isListed) {
            if (packageVersion.isDeleted) {
                return Utils_String.format(
                    PackageResources.PackageDeletedMessage,
                    packageVersion.version,
                    packageSummary.name
                );
            } else {
                return Utils_String.format(
                    PackageResources.PackageUnlistedMessage,
                    packageVersion.version,
                    packageSummary.name
                );
            }
        }

        return null;
    }

    public getOverviewAttributesPanel(
        feed: Feed,
        packageSummary: Package,
        packageVersion: PackageVersion,
        packageVersionMetrics: PackageVersionMetrics,
        packageMetrics: PackageMetrics,
        isSmartDependenciesEnabled: boolean,
        isProvenanceEnabled: boolean,
        isMetricsEnabled: boolean,
        isMetricsLoading: boolean
    ): React.ReactNode {
        return React.createElement(NuGetOverviewAttributesPanel, {
            pageContext: this.pageContext,
            feed,
            packageSummary,
            packageVersion,
            protocol: this,
            packageVersionMetrics,
            packageMetrics,
            isSmartDependenciesEnabled,
            isProvenanceEnabled,
            isMetricsEnabled,
            isMetricsLoading
        } as IPackageDetailsProps);
    }

    public getOverviewContentPanel(
        feed: Feed,
        feedViews: FeedView[],
        packageSummary: Package,
        packageVersion: PackageVersion
    ): React.ReactNode {
        return React.createElement(NuGetOverviewContentPanel, {
            pageContext: this.pageContext,
            feed,
            feedViews,
            packageSummary,
            packageVersion,
            protocol: this
        } as IPackageDetailsProps);
    }

    public getMultiSelectPackageCommands(
        feed: Feed,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[]
    ): IPackageCommand[] {
        const packageCommands = new Array<IPackageCommand>();
        let cachedPackagesIncluded: boolean = false;

        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;

        // Trim out npm cached packages for V1 feeds. (Must do it here as well because there is a chance of mixed protocols)
        if (!isV2Feed(feed)) {
            if (selectedPackages !== null) {
                const selectedPackagesWithoutCached: Package[] = selectedPackages.filter(
                    (pkg: Package) => pkg.isCached !== true && pkg.versions[0].isCachedVersion !== true
                );
                if (selectedPackagesWithoutCached.length < selectedPackages.length) {
                    cachedPackagesIncluded = true;
                    selectedPackages = selectedPackagesWithoutCached;
                }
            } else if (selectedVersions !== null) {
                const selectedVersionsWithoutCached: PackageVersion[] = selectedVersions.filter(
                    (version: PackageVersion) => version.isCachedVersion !== true
                );
                if (selectedVersionsWithoutCached.length < selectedVersions.length) {
                    cachedPackagesIncluded = true;
                    selectedVersions = selectedVersionsWithoutCached;
                }
            }
        }

        let isListed: boolean = false;
        if (selectedPackages && selectedPackages.length > 0) {
            // Promote
            // Only add Promote button on package list for v1
            packageCommands.push(new PromotePackagesCommand(selectedPackages, showDisabledCommands));

            isListed = selectedPackages.some((pkg: Package) => pkg.versions[0].isListed);
        } else if (selectedVersions) {
            isListed = selectedVersions.some((version: PackageVersion) => version.isListed);
        }

        if (!isListed) {
            // Relist
            const relistCommands = this._handleUnlistCommand(
                false,
                selectedPackages,
                selectedVersions,
                showDisabledCommands
            );
            packageCommands.push(...relistCommands);
        } else {
            // Unlist
            const unlistCommands = this._handleUnlistCommand(
                true,
                selectedPackages,
                selectedVersions,
                showDisabledCommands
            );
            packageCommands.push(...unlistCommands);
        }

        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            NuGetKey,
            selectedPackages,
            selectedVersions,
            null /*packageName*/,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/,
            cachedPackagesIncluded
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    // Get a list of available package commands for this protocol based on the user's role
    public getPackageCommands(
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean = false
    ): IPackageCommand[] {
        const packageCommands = new Array<IPackageCommand>();
        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);

        if (packageVersion.isDeleted) {
            return packageCommands;
        }

        const feedId = getFullyQualifiedFeedId(feed);
        // Download
        // If we make the call to get the url after the button is clicked, the browser will block the new window popup.
        // So we get the download url as soon as the package commands show up, this way the promise will already be resolved when the button is clicked.
        const nuGetDataService = this.pageContext.getService<IFeedNuGetDataService>("IFeedNuGetDataService");
        const getDownloadPackageVersionContentUrl = () =>
            nuGetDataService.getDownloadPackageVersionContentUrl(
                feedId,
                packageSummary.normalizedName,
                packageVersion.normalizedVersion,
                feed && feed.project != null
            );
        const downloadPackageCommand = new DownloadPackageCommand(
            DownloadPackageCommand.onDownloadPackage,
            getDownloadPackageVersionContentUrl,
            packageSummary,
            packageVersion,
            feed,
            viaPackageList
        );
        packageCommands.push(downloadPackageCommand);

        // Promote
        const showDisabledCommands = role === FeedRole.None || role === FeedRole.Reader;
        packageCommands.push(new PromotePackageCommand(packageSummary, viaPackageList, showDisabledCommands));

        if (!packageVersion.isListed) {
            // Relist
            packageCommands.push(
                new RelistPackageCommand(
                    this.pageContext,
                    RelistPackageCommand.onRelistPackage,
                    packageSummary,
                    packageVersion,
                    feed,
                    viaPackageList,
                    showDisabledCommands
                )
            );
        } else {
            // Unlist
            packageCommands.push(
                new UnlistPackageCommand(
                    this.pageContext,
                    UnlistPackageCommand.onUnlistPackage,
                    packageSummary,
                    packageVersion,
                    feed,
                    viaPackageList,
                    showDisabledCommands
                )
            );
        }

        // Delete
        const selectedPackages: Package[] = viaPackageList ? [packageSummary] : null;
        const selectedVersions: PackageVersion[] = viaPackageList ? null : [packageVersion];
        const deleteCommands = DeleteCommandHelper.addDeleteCommands(
            NuGetKey,
            selectedPackages,
            selectedVersions,
            packageSummary.name,
            !PermissionHelper.isAdministratorFromRole(role) /*disabled*/
        );
        packageCommands.push(...deleteCommands);

        return packageCommands;
    }

    public supportedFeatures(feed?: Feed): ISupportedFeatures {
        const validCapability = feed ? isV2Feed(feed) : true;
        const features: ISupportedFeatures = {
            upstreams: validCapability,
            projectScopedFeeds: true,
            publicUpstreams: true
        };
        return features;
    }

    // Use supportedFeatures instead.
    // Except for connect to feed dialog that is located in the old Feed/Service/Web location.
    public supportsUpstreams(feed?: Feed): boolean {
        const validCapability = feed ? isV2Feed(feed) : true;
        return validCapability;
    }

    public async deleteLatestVersions(projectId: string, feed: Feed, pkgs: Package[]): Promise<void> {
        try {
            const iterator = new TransformingBatchGenerator(pkgs, this.NuGetDataService.batchSize, (pkg: Package) => {
                return {
                    id: pkg.name,
                    version: pkg.versions[0].normalizedVersion
                };
            });
            await this.NuGetDataService.batchDelete(projectId, feed.id, iterator);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public deletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void> {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.NuGetDataService.batchSize,
            (packageVersion: PackageVersion) => {
                return {
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                };
            }
        );
        return this.NuGetDataService.batchDelete(projectId, feed.id, iterator);
    }

    public restorePackageVersionsToFeed(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void> {
        const iterator = new TransformingBatchGenerator(
            versions,
            this.NuGetDataService.batchSize,
            (packageVersion: PackageVersion) => {
                return {
                    id: pkg.name,
                    version: packageVersion.normalizedVersion
                };
            }
        );
        return this.NuGetDataService.batchRestoreToFeed(projectId, feed.id, iterator);
    }

    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ) {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.NuGetDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.NuGetDataService.batchSize);
        }

        return this.NuGetDataService.batchPermanentlyDelete(projectId, feed.id, iterator);
    }

    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public async promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ): Promise<void> {
        let iterator: IBatchIterator<MinimalPackageDetails[]>;
        if (isPackage(pkgOrDetails)) {
            const pkgName = pkgOrDetails.name;
            iterator = new TransformingBatchGenerator(
                versions,
                this.NuGetDataService.batchSize,
                packageVersion => <MinimalPackageDetails>{ id: pkgName, version: packageVersion.normalizedVersion }
            );
        } else {
            iterator = new BatchGenerator(pkgOrDetails, this.NuGetDataService.batchSize);
        }

        await this.NuGetDataService.batchPromotePackages(projectId, feed.id, feedView.id, iterator);
    }

    public getCopyInstallCommand(
        feed: Feed,
        pkgName: string,
        pkgVersion: PackageVersion,
        separator: string = " "
    ): string {
        // Semver 2 check
        if (pkgVersion.version.indexOf("+") === -1) {
            return `Install-Package ${pkgName}${separator}-version ${pkgVersion.version}`;
        } else {
            return `Install-Package ${pkgName}${separator}-version ${pkgVersion.normalizedVersion}`;
        }
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        return this.NuGetDataService.getUpstreamingBehavior(feed.id, pkg.name, projId(feed));
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        return this.NuGetDataService.setUpstreamingBehavior(feed.id, pkg.name, behavior, projId(feed));
    }

    private _getUnlistDialogProps(
        headerText: string,
        confirmText: string,
        saveButtonText: string,
        savingButtonText: string,
        onSaveCallback: () => void,
        onDismissCallback: () => void
    ): IGeneralDialogProps {
        const dialogProps: IGeneralDialogProps = {
            headerText,
            confirmText,
            saveButtonText,
            confirmTextLearnMoreLink: "https://docs.microsoft.com/en-us/vsts/package/nuget/unlist-delete",
            onSavePrimaryButtonText: savingButtonText,
            onSaveCallback,
            onDismissCallback
        };

        return dialogProps;
    }

    private _handleUnlistCommand(
        unlist: boolean,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[],
        disabled?: boolean
    ): IPackageCommand[] {
        let displayText: string = null;
        const packageCommands: IPackageCommand[] = [];
        const saveButtonText = unlist
            ? PackageResources.PackageCommands_UnlistPackage
            : PackageResources.PackageCommands_RelistPackage;
        const savingButtonText = unlist
            ? PackageResources.PackageCommands_UnlistPackage_Unlisting
            : PackageResources.PackageCommands_RelistPackage_Relisting;
        const onSaveCallback = () =>
            Actions.PackageListedChanged.invoke({
                selectedPackages,
                selectedVersions,
                isListed: unlist ? false : true
            });
        const onDismissCallback = () => Actions.DialogOpenChanged.invoke(null);

        if (selectedVersions) {
            // Unlist/Relist selected versions
            displayText = unlist
                ? PackageResources.PackageCommands_UnlistPackage
                : PackageResources.PackageCommands_RelistPackage;
            const headerText = Utils_String.format(
                unlist
                    ? PackageResources.UnlistDialog_HeaderText_VersionsList
                    : PackageResources.RelistDialog_HeaderText_VersionsList,
                selectedVersions.length
            );
            const confirmText = unlist
                ? PackageResources.UnlistDialog_ConfirmText_VersionsList
                : PackageResources.RelistDialog_ConfirmText_VersionsList;

            const unlistDialogProps = this._getUnlistDialogProps(
                headerText,
                confirmText,
                saveButtonText,
                savingButtonText,
                onSaveCallback,
                onDismissCallback
            );
            packageCommands.push(
                unlist
                    ? new UnlistPackagesCommand(PackageCommandIds.Unlist, displayText, unlistDialogProps, disabled)
                    : new RelistPackagesCommand(PackageCommandIds.Relist, displayText, unlistDialogProps, disabled)
            );
        } else if (selectedPackages) {
            if (selectedPackages.length > 1) {
                // Unlist latest version
                const unlistLatestDialogProps = this._getUnlistDialogProps(
                    unlist
                        ? PackageResources.UnlistDialog_HeaderText_LatestVersion
                        : PackageResources.RelistDialog_HeaderText_LatestVersion,
                    unlist
                        ? PackageResources.UnlistDialog_ConfirmText_LatestVersion
                        : PackageResources.RelistDialog_ConfirmText_LatestVersion,
                    saveButtonText,
                    savingButtonText,
                    onSaveCallback,
                    onDismissCallback
                );
                displayText = unlist
                    ? PackageResources.PackageCommands_UnlistPackage_Latest
                    : PackageResources.PackageCommands_RelistPackage_Latest;
                packageCommands.push(
                    unlist
                        ? new UnlistPackagesCommand(
                              PackageCommandIds.UnlistLatest,
                              displayText,
                              unlistLatestDialogProps,
                              disabled
                          )
                        : new RelistPackagesCommand(
                              PackageCommandIds.RelistLatest,
                              displayText,
                              unlistLatestDialogProps,
                              disabled
                          )
                );
            }
        }

        return packageCommands;
    }
}
